import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { copyToClipboard } from '~/utils/clipboard';
import { cn } from '~/utils/cn';

interface Props {
  /** Value copied to the clipboard. Empty/blank disables the button — there is
   *  nothing useful to put on the clipboard. */
  text: string | null | undefined;
  /** Accessible name; also the tooltip (native `title` unless `tooltip` is set). */
  label: string;
  /** Toast shown on success. */
  copyMessage?: string;
  className?: string | undefined;
  /** Show `label` in the app's styled Tooltip instead of the native `title`. */
  tooltip?: boolean;
  /** `data-testid` for the button (default `copy-button`). */
  testId?: string;
}

/**
 * Icon-only click-to-copy button, for places where the copied value is already
 * on screen (panel headers, table cells) and repeating it inline would be noise.
 * Use `CopyableText` instead when the value itself still needs rendering.
 */
export function CopyIconButton({
  text,
  label,
  copyMessage = 'Copied to clipboard',
  className,
  tooltip = false,
  testId = 'copy-button',
}: Props) {
  const [copied, setCopied] = useState(false);
  const isEmpty = !text || text.trim() === '';

  function handleCopy() {
    if (isEmpty) return;
    copyToClipboard(text);
    setCopied(true);
    toast.success(copyMessage);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  }

  const button = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      disabled={isEmpty}
      aria-label={label}
      data-testid={testId}
      {...(tooltip ? {} : { title: label })}
      onClick={(e) => {
        // Copy buttons often sit inside an accordion trigger or a link — don't
        // let the click bubble into toggling/navigating.
        e.stopPropagation();
        handleCopy();
      }}
      className={cn(
        'size-6 shrink-0',
        isEmpty ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
    >
      {copied ? (
        <Check className="text-success size-3.5" aria-hidden="true" />
      ) : (
        <Copy className="size-3.5" aria-hidden="true" />
      )}
    </Button>
  );

  if (!tooltip) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
